import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import SimpleCard from '../../../../design-system/card/simple-card/SimpleCard';
import ProgressBar from '../../../../design-system/progress-bar/ProgressBar';
import Tooltip from '../../../../design-system/tooltip/Tooltip';
import styles from './SocialFeaturesContainer.module.scss';

/**
 * SocialFeaturesContainer - Displays social accountability features
 * Shows one of three features based on feature flags:
 * 1. Peer Milestones - Shows how many teammates booked sessions
 * 2. Achievement Badges - Shows progress toward badges
 * 3. Streak Reminders - Shows the user's daily streak
 */
const SocialFeaturesContainer = ({ socialFeaturesData, showPeerMilestones, showAchievementBadges, showStreakReminders }) => {
  // Extract data with fallback values
  const {
    teammatesNumber = 0,
    streakDays = 0,
    badgeName = 'Consistency Champion',
    badgeProgress = 0,
    badgeTotal = 5,
    isLoading = false
  } = socialFeaturesData?.data || {};

  /**
   * Get dynamic peer milestone message based on teammatesNumber
   */
  const getPeerMilestoneMessage = (count) => {
    if (count === 0) {
      return 'No one from your team has booked a class this week. Be the first and set a great example!';
    } else if (count === 1) {
      return 'This week 1 teammate has booked a session!';
    } else {
      return `This week ${count} of your teammates booked their sessions!`;
    }
  };

  /**
   * Get dynamic header for peer milestone message
   */
  const getPeerMilestoneHeader = (count) => {
    if (count === 0) {
      return 'Lead The Way!';
    } else {
      return 'Keep the momentum going!';
    }
  };

  /**
   * Render peer milestones card
   * Shows how many teammates booked sessions this week
   */
  const renderPeerMilestones = () => (
    <SimpleCard
      isLoading={isLoading}
      cardContent={
        <div className={styles.progressCardWrapper}>
          <p className={styles.socialHeader}>{getPeerMilestoneHeader(teammatesNumber)}</p>
          <p className={styles.socialBody}>{getPeerMilestoneMessage(teammatesNumber)}</p>
          <div className={styles['updated-daily']}>
            <Tooltip tooltipLabel="This information is updated daily." backgroundColor="white" position="bottom-right" />
          </div>
        </div>
      }
    />
  );

  /**
   * Render achievement badges card
   * Shows progress toward earning badges
   */
  const renderAchievementBadges = () => {
    if (badgeName === null) {
      return;
    }
    return (
      <SimpleCard
        isLoading={isLoading}
        cardContent={
          <div className={styles.progressCardWrapper}>
            <p className={styles.socialHeader}>Next Badge:</p>
            <div className={styles.badgeContent}>
              <p className={styles.badgeName}>🏆 {badgeName}</p>
              <p className={styles.badgeDescription}>Complete {badgeTotal - badgeProgress} more Language Practices to unlock this badge</p>
              <div className={styles.progressWrapper}>
                <ProgressBar progress={badgeProgress / badgeTotal} isLoading={isLoading} />
                <br />
                <span className={styles.badgeDescription}>
                  ({badgeProgress} / {badgeTotal} LPs)
                </span>
              </div>
            </div>
          </div>
        }
      />
    );
  };

  /**
   * Render streak reminders card
   * Shows the user's current streak
   */
  const renderStreakReminders = () => (
    <SimpleCard
      isLoading={isLoading}
      cardContent={
        <div className={styles.progressCardWrapper}>
          <p className={styles.socialHeader}>🔥 {streakDays} Day Streak!</p>
          <p className={styles.socialBody}>Come back tomorrow to keep it going!</p>
        </div>
      }
    />
  );

  /**
   * Determine which component to render based on feature flags
   */
  const renderFeatureCard = () => {
    if (showPeerMilestones) {
      return renderPeerMilestones();
    } else if (showAchievementBadges) {
      return renderAchievementBadges();
    } else if (showStreakReminders) {
      return renderStreakReminders();
    }

    // Return null if no feature is enabled
    return null;
  };

  return <div className={styles.cardsContainer}>{renderFeatureCard()}</div>;
};

SocialFeaturesContainer.propTypes = {
  socialFeaturesData: PropTypes.object,
  showPeerMilestones: PropTypes.bool,
  showAchievementBadges: PropTypes.bool,
  showStreakReminders: PropTypes.bool
};

SocialFeaturesContainer.defaultProps = {
  showPeerMilestones: false,
  showAchievementBadges: false,
  showStreakReminders: false
};

const mapStateToProps = (state) => ({
  socialFeaturesData: state.socialFeaturesData
});

export default connect(mapStateToProps)(SocialFeaturesContainer);
